import { useEffect, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import classNames from 'classnames'
import { PathUtils } from 'routes/routes.utils'

import { useScreenWidth } from 'app/hooks/useScreenWidth'
import { useAuth } from 'app/providers/auth/AuthProvider'

import BackIcon from 'assets/icons/back.svg?react'
import logo from 'assets/images/logo-text.svg'

import { Button } from 'common/components/Button/Button'
import { Show } from 'common/components/Show/Show'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { APP_CONSTANTS } from 'common/constants/constants'
import { useLazyFetchSitesQuery } from 'common/store/api/rootApi'

import { AUTH_CONSTANTS } from 'features/Auth/constants/authConstants'
import { CartIcon } from 'features/Cart'

import { Widget } from 'features/Widget'

import { Tooltip } from 'antd'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'
import styles from './header.module.scss'
import HeaderLinks from './HeaderLinks'

const Header = () => {
  const {
    user,
    isSuperAdminAsDoctor,
    updateName,
    logoutAsDoctor,
    hasCompletedDocuments,
    updateDocuments,
    isSidebarVisible,
    isStaff,
    toggleIsSidebarVisible,
  } = useAuth()
  const { isMobile } = useScreenWidth()

  const navigate = useNavigate()
  const params = useParams()
  const headerRef = useRef<HTMLElement>(null)

  const [fetchSites] = useLazyFetchSitesQuery()
  const { data: profileData } = useFetchProfileQuery(null, { skip: !user })
  const profile = profileData?.profile

  useEffect(() => {
    if (profileData) {
      updateDocuments(profileData.documents)
      updateName(profile?.first_name, profile?.last_name)
    }
  }, [profileData])

  useEffect(() => {
    fetchSites()
    const handleScroll = () => {
      if (window.scrollY > 0 && headerRef && headerRef.current)
        headerRef.current.classList.add(styles.parentSticky)
      else if (headerRef?.current?.classList?.contains(styles.parentSticky))
        headerRef.current.classList.remove(styles.parentSticky)
    }
    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleGoBack = (): void => {
    navigate(-1)
  }

  const fullName = (() => {
    if (!profile?.first_name) return 'Unvalidated User'
    return `${profile.salutation || ''} ${profile.last_name}, ${profile.first_name}`
  })()

  return (
    <header ref={headerRef} className={styles.parent} id='app-header'>
      <Show when={user}>
        <Button
          className={classNames(styles.parentBurgerMenu, {
            [styles.parentBurgerMenuActive]: isSidebarVisible,
          })}
          onClick={toggleIsSidebarVisible}
          modifier={BUTTON_MODIFIER.DEFAULT}
          proportion={BUTTON_PROPORTION.MEDIUM}>
          <div className={styles.parentHamburgerLines}>
            <span className={classNames(styles.parentHamburgerLine, styles.parentHamburgerLine1)} />
            <span className={classNames(styles.parentHamburgerLine, styles.parentHamburgerLine2)} />
            <span className={classNames(styles.parentHamburgerLine, styles.parentHamburgerLine3)} />
          </div>
        </Button>
      </Show>
      <Show when={!isMobile}>
        <button
          type='button'
          className={styles.parentSidebarLogoButton}
          onClick={() => (window.location.href = AUTH_CONSTANTS.MAIN_WEBSITE_URL)}
          aria-label={ALT_CONSTANTS.LOGO_ICON}>
          <img className={styles.parentSidebarLogo} src={logo} alt={ALT_CONSTANTS.LOGO_ICON} />
        </button>
      </Show>
      <Show
        when={params?.tabKey && params?.ticketId}
        fallback={
          <>
            {profile && (
              <span className={styles.parentWelcome}>
                {isSuperAdminAsDoctor ? APP_CONSTANTS.LOGGED_IN_AS : APP_CONSTANTS.WELCOME}
                <span className={styles.parentName} data-admin={isSuperAdminAsDoctor}>
                  <Tooltip title={fullName} placement='bottomLeft'>
                    {fullName}
                  </Tooltip>
                </span>
                {isSuperAdminAsDoctor && (
                  <Button
                    proportion={BUTTON_PROPORTION.SMALL}
                    modifier={BUTTON_MODIFIER.TERTIARY}
                    className={styles.parentBackDoctor}
                    onClick={logoutAsDoctor}>
                    {APP_CONSTANTS.BACK}
                  </Button>
                )}
              </span>
            )}
          </>
        }>
        <Button onClick={handleGoBack} className='reset-button center'>
          <BackIcon />
        </Button>
      </Show>

      <div
        className={styles.parentContent}
        onClick={() => isMobile && isSidebarVisible && toggleIsSidebarVisible()}>
        <HeaderLinks />
        <Show when={hasCompletedDocuments || isStaff}>
          <CartIcon />
        </Show>

        <Show when={!user} fallback={<Widget userProfile={profileData} />}>
          <Link to={PathUtils.getLogin()}>
            <Button>{AUTH_CONSTANTS.LOG_IN}</Button>
          </Link>
        </Show>
      </div>
    </header>
  )
}

export default Header
